import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth } from "../firebase";
import "./SettingsPanel.css";

const RADIUS_OPTIONS = [2, 5, 10, 25, 50, 100];

export default function SettingsPanel() {
  const [radiusKm, setRadiusKm] = useState(50);
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configDoc = await getDoc(doc(db, "adminConfig", "leadSharing"));
        if (configDoc.exists()) {
          const data = configDoc.data();
          setRadiusKm(data.radiusKm || 50);
          setIsEnabled(data.isEnabled !== false);
          if (data.updatedAt) {
            const date = data.updatedAt.toDate
              ? data.updatedAt.toDate()
              : new Date(data.updatedAt);
            setLastUpdated(date.toLocaleString());
          }
        }
      } catch (err) {
        console.error("Failed to load lead sharing config:", err);
      }
      setLoading(false);
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "adminConfig", "leadSharing"), {
        radiusKm,
        isEnabled,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "",
      });
      setLastUpdated(new Date().toLocaleString());
    } catch (err) {
      console.error("Failed to save lead sharing config:", err);
      alert("Failed to save settings. Please try again.");
    }
    setSaving(false);
  };

  const sectionHeader = (
    <div className="card-header">
      <div className="sp-section-heading">
        <h2 className="card-title" id="sp-lead-title">Nearby lead sharing</h2>
        <p className="sp-section-desc">
          Control how leads are distributed to nearby dealers when a tenant shows interest in a property.
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="sp-panel">
        <section className="card sp-section" aria-labelledby="sp-lead-title" aria-busy="true">
          {sectionHeader}
          <div className="sp-rows">
            {[0, 1].map((i) => (
              <div className="sp-row" key={i}>
                <div className="sp-row-text">
                  <div className="skeleton sp-skel-label" />
                  <div className="skeleton sp-skel-hint" />
                </div>
                <div className={`skeleton ${i === 0 ? "sp-skel-switch" : "sp-skel-control"}`} />
              </div>
            ))}
          </div>
          <div className="sp-section-footer">
            <div className="skeleton sp-skel-hint" />
            <div className="skeleton sp-skel-button" />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="sp-panel">
      <section className="card sp-section" aria-labelledby="sp-lead-title">
        {sectionHeader}

        <div className="sp-rows">
          {/* Enable/Disable Toggle */}
          <div className="sp-row">
            <div className="sp-row-text">
              <label className="sp-row-label" htmlFor="sp-lead-enabled">Enable lead sharing</label>
              <span className="sp-row-hint" id="sp-lead-enabled-hint">
                When on, nearby dealers receive shared leads automatically.
              </span>
            </div>
            <label className="sp-switch">
              <input
                id="sp-lead-enabled"
                type="checkbox"
                role="switch"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                aria-describedby="sp-lead-enabled-hint"
              />
              <span className="sp-switch-track" aria-hidden="true" />
            </label>
          </div>

          {/* Radius Selection */}
          <div className={`sp-row ${isEnabled ? "" : "is-disabled"}`}>
            <div className="sp-row-text">
              <span className="sp-row-label" id="sp-radius-label">Lead sharing radius</span>
              <span className="sp-row-hint" id="sp-radius-hint">
                {isEnabled
                  ? "Dealers within this distance from the property receive the lead."
                  : "Turn on lead sharing to change the radius."}
              </span>
            </div>
            <div
              className="segmented sp-radius"
              role="group"
              aria-labelledby="sp-radius-label"
              aria-describedby="sp-radius-hint"
            >
              {RADIUS_OPTIONS.map((km) => (
                <button
                  key={km}
                  type="button"
                  aria-pressed={radiusKm === km}
                  onClick={() => setRadiusKm(km)}
                  disabled={!isEnabled}
                >
                  {km} km
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="sp-section-footer">
          <span className="sp-meta">
            {lastUpdated ? `Last updated ${lastUpdated}` : ""}
          </span>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving && <span className="btn-spinner" aria-hidden="true" />}
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </section>
    </div>
  );
}
